import { useEffect, useState } from 'react';
import { mensajeDeError } from '@/core/red/conectividad';
import {
  deactivateSplitTemplate,
  listParticipants,
  listSplitTemplates,
  saveSplitTemplate,
  setOrganizationDefaultTemplate,
  type Participant,
  type SplitDraft,
  type SplitTemplate,
} from '@/features/works/works';
import { fetchOrganizationSettings } from '@/features/clients/clients';
import { CampoNumero } from '@/ui/CampoNumero';

/**
 * Repartos habituales de la organización.
 *
 * El modelo de plantillas y la precedencia —trabajo › cliente › organización—
 * ya existían y el formulario de trabajo ya las ofrecía como chips. Lo único
 * que faltaba era esta pantalla: no había forma de crear una plantilla ni de
 * elegir cuál es la habitual, así que la función estaba construida y apagada.
 *
 * Nada de esto se codifica en la aplicación. Una veterinaria de una persona va
 * a tener «100 % yo»; otra, «50 / 50»; otra, tres socios con porcentajes que
 * sólo ellos conocen. Poner un reparto en el código sería decidir por todas.
 */
export function PlantillasCard({
  organizationId,
  isAdmin,
}: {
  organizationId: string;
  isAdmin: boolean;
}) {
  const [participantes, setParticipantes] = useState<Participant[]>([]);
  const [plantillas, setPlantillas] = useState<SplitTemplate[]>([]);
  const [habitual, setHabitual] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);

  // Borrador de la plantilla que se está creando o editando.
  const [editando, setEditando] = useState<string | null>(null);
  const [nombre, setNombre] = useState('');
  const [lineas, setLineas] = useState<Record<string, number | null>>({});
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    let cancelado = false;
    void (async () => {
      try {
        const [ps, ts, s] = await Promise.all([
          listParticipants(organizationId),
          listSplitTemplates(organizationId),
          fetchOrganizationSettings(organizationId),
        ]);
        if (cancelado) return;
        setParticipantes(ps);
        setPlantillas(ts);
        setHabitual(s?.default_split_template_id ?? null);
      } catch (e) {
        if (!cancelado) setError(mensajeDeError(e));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [organizationId, recarga]);

  const suma = Object.values(lineas).reduce<number>((a, v) => a + (v ?? 0), 0);
  // El mismo criterio que la base: cuatro decimales, ni uno más.
  const sumaOk = Math.round(suma * 10000) === 1000000;

  const empezarNueva = () => {
    setEditando('nueva');
    setNombre('');
    setLineas({});
  };

  const empezarEdicion = (t: SplitTemplate) => {
    setEditando(t.id);
    setNombre(t.name);
    setLineas(Object.fromEntries(t.lines.map((l) => [l.participant_id, l.percentage])));
  };

  const guardar = async () => {
    setError(null);
    if (nombre.trim() === '') {
      setError('Poné un nombre para reconocerla después');
      return;
    }
    if (!sumaOk) {
      setError(`Los porcentajes tienen que sumar 100. Ahora suman ${suma}`);
      return;
    }
    setGuardando(true);
    try {
      const lines: SplitDraft[] = Object.entries(lineas)
        .filter(([, v]) => (v ?? 0) > 0)
        .map(([participant_id, v], i) => ({
          participant_id,
          percentage: v as number,
          sort_order: i + 1,
        }));
      await saveSplitTemplate({
        organizationId,
        name: nombre.trim(),
        lines,
        ...(editando !== 'nueva' && editando !== null ? { id: editando } : {}),
      });
      setEditando(null);
      setRecarga((x) => x + 1);
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  };

  const marcarHabitual = async (id: string | null) => {
    setError(null);
    try {
      await setOrganizationDefaultTemplate(organizationId, id);
      setHabitual(id);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };

  const desactivar = async (id: string) => {
    setError(null);
    try {
      if (habitual === id) await setOrganizationDefaultTemplate(organizationId, null);
      await deactivateSplitTemplate(id);
      setRecarga((x) => x + 1);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };

  return (
    <section className="flex flex-col gap-3 superficie p-4" data-testid="plantillas">
      <div>
        <h2 className="font-medium">Repartos habituales</h2>
        <p className="text-xs text-[var(--tinta-suave)]">
          El reparto marcado como habitual viene puesto al crear un trabajo, y siempre se puede
          cambiar en ese trabajo. Un cliente puede tener el suyo propio.
        </p>
      </div>

      {error && (
        <p role="alert" className="text-sm text-[var(--peligro)]" data-testid="plantillas-error">
          {error}
        </p>
      )}

      <ul className="flex flex-col divide-y divide-[var(--linea)]">
        {plantillas.map((t) => (
          <li key={t.id} className="flex items-center gap-2 py-3" data-testid="plantilla">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">
                {t.name}
                {habitual === t.id && (
                  <span className="ml-2 rounded-full bg-[var(--exito-fondo)] px-2 py-0.5 text-xs text-[var(--exito)]">
                    habitual
                  </span>
                )}
              </p>
              <p className="truncate text-xs text-[var(--tinta-suave)]">
                {t.lines
                  .map(
                    (l) =>
                      `${participantes.find((p) => p.id === l.participant_id)?.display_name ?? '?'} ${l.percentage} %`,
                  )
                  .join(' · ')}
              </p>
            </div>
            {isAdmin && (
              <div className="flex shrink-0 gap-2">
                {habitual !== t.id && (
                  <button
                    type="button"
                    onClick={() => void marcarHabitual(t.id)}
                    className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-xs"
                    data-testid="plantilla-habitual"
                  >
                    Hacer habitual
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => empezarEdicion(t)}
                  className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-xs"
                  data-testid="plantilla-editar"
                >
                  Editar
                </button>
                <button
                  type="button"
                  onClick={() => void desactivar(t.id)}
                  className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-xs"
                  data-testid="plantilla-quitar"
                >
                  Quitar
                </button>
              </div>
            )}
          </li>
        ))}
        {plantillas.length === 0 && (
          <li className="py-3 text-sm text-[var(--tinta-suave)]">
            Todavía no hay ninguno. Creá el que uses siempre y no vas a tener que armarlo en cada
            trabajo.
          </li>
        )}
      </ul>

      {isAdmin && editando === null && (
        <button
          type="button"
          onClick={empezarNueva}
          className="min-h-11 self-start rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] border-dashed px-3 text-sm"
          data-testid="plantilla-nueva"
        >
          + Nuevo reparto habitual
        </button>
      )}

      {isAdmin && editando !== null && (
        <div className="flex flex-col gap-3 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] p-3">
          <label className="flex flex-col gap-1 text-sm">
            Nombre
            <input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej. Habitual"
              className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-base"
              data-testid="plantilla-nombre"
            />
          </label>

          {participantes.map((p) => (
            <div key={p.id} className="flex items-center gap-2">
              <span className="flex-1 text-sm">{p.display_name}</span>
              <CampoNumero
                min={0}
                max={100}
                step="0.0001"
                placeholder="0"
                value={lineas[p.id] ?? null}
                onChange={(n) => setLineas((prev) => ({ ...prev, [p.id]: n }))}
                className="min-h-11 w-24 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-right text-base"
                data-testid="plantilla-porcentaje"
              />
              <span className="text-sm text-[var(--tinta-suave)]">%</span>
            </div>
          ))}

          <p
            className={`text-sm ${sumaOk ? 'text-[var(--exito)]' : 'text-[var(--peligro)]'}`}
            data-testid="plantilla-suma"
          >
            Suma {suma} % {sumaOk ? '✓' : '✗'}
          </p>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void guardar()}
              disabled={guardando}
              className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-4 text-sm disabled:opacity-50"
              data-testid="plantilla-guardar"
            >
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
            <button
              type="button"
              onClick={() => setEditando(null)}
              className="min-h-11 rounded-lg px-4 text-sm underline opacity-70"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
